import React, { useState } from 'react';

import { Helmet } from 'react-helmet';

import { Alert, Box, Chip, Container, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetChains } from '~/hooks/apis/reservation/useGetChains';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';

import { Chain } from './types';

interface Props {
  basePath?: string;
  embedded?: boolean;
}

export default function ChainsListPage({ basePath = '/commercial-ops/chains', embedded = false }: Props) {
  const { push } = useAppNavigation();
  const [searchName, setSearchName] = useState('');
  const debouncedSearch = useDebounced(searchName, 400);

  const { data: chains = [], isLoading, isError } = useGetChains(debouncedSearch || undefined);

  const columns: Array<GridColDef<Chain>> = [
    {
      field: 'name',
      headerName: 'Chain Name',
      flex: 2,
      display: 'flex',
      minWidth: 200,
    },
    {
      field: 'sfid',
      headerName: 'Salesforce ID',
      flex: 1.5,
      display: 'flex',
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      display: 'flex',
      renderCell: (params) => (
        <Chip label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} size="small" />
      ),
    },
  ];

  const content = (
    <>
      <Helmet>
        <title>Chains</title>
      </Helmet>
      {!embedded && <PageHeader title="Chains" />}

      <Stack spacing={2}>
        <TextField
          label="Search chains"
          placeholder="Search by chain name..."
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          size="small"
          fullWidth
        />

        {isError && <Alert severity="error">Failed to load chains</Alert>}

        <Box sx={{ width: '100%' }}>
          <DataGrid
            columns={columns}
            rows={chains}
            loading={isLoading}
            autoHeight
            pageSizeOptions={[25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            onRowClick={(params) => push(`${basePath}/${params.row.id}`)}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{ toolbar: { showQuickFilter: true } }}
            disableColumnFilter
            disableDensitySelector
          />
        </Box>
      </Stack>
    </>
  );

  if (embedded) {
    return content;
  }

  return <Container>{content}</Container>;
}
